import { MENTION_HREF_RE, type CommentBodyFormat } from './document-blocks'

/**
 * Comment bodies (PLAN margin comments §6), shared by web and worker: a tiny markdown subset —
 * `**bold**`, `*italic*`, `__underline__` (we own both ends: `__` is never
 * bold here), `[text](url)` with http(s)/mailto only, `\` escapes, and a
 * member mention `[@Name](user:<userId>)` (board 9p2d) — the same syntax as
 * document mentions (worker/lib/blocks-markdown.ts). No HTML, headings or
 * lists. Old comments are `format: 'text'` and never parsed.
 *
 * This module is both ends: `serializeCommentBody` writes what the composer
 * produced, `parseCommentBody` reads it back for rendering, the agent prompt
 * and notifications (`stripCommentMarkdown`, also used by the DocumentStore).
 * A marker without a closer renders as written (`2 * 3`), on both sides.
 */

export interface CommentSpan {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  /** Set on every span inside a link. */
  href?: string
  /**
   * An @-mention chip. `text` is the plain fallback `@Name` (what
   * `stripCommentMarkdown` and notifications show); formats never apply.
   */
  mention?: CommentMention
}

/** A project member mentioned in a comment body. */
export interface CommentMention {
  userId: string
  /** Name at the time of writing (the chip shows the live name while they are a member). */
  name: string
}

/** One paragraph of the composer's output. */
export type CommentLine = CommentSpan[]

const SAFE_HREF = /^(https?:\/\/|mailto:)/i
const ESCAPABLE = new Set(['\\', '*', '_', '[', ']', '(', ')'])

type Format = { bold: boolean; italic: boolean; underline: boolean }

/** Whether `marker` occurs (unescaped) in `s` at or after `from`. */
function hasCloser(s: string, marker: string, from: number): boolean {
  let i = from
  while (i < s.length) {
    if (s[i] === '\\') {
      i += 2
      continue
    }
    if (s.startsWith(marker, i)) return true
    i++
  }
  return false
}

/** Index of the unescaped `close` matching an open bracket at `from`, or -1. */
function findBracketClose(s: string, from: number, close: string): number {
  let i = from
  while (i < s.length) {
    if (s[i] === '\\') {
      i += 2
      continue
    }
    if (s[i] === close) return i
    i++
  }
  return -1
}

function parseInline(s: string, href: string | undefined, out: CommentSpan[]): void {
  const format: Format = { bold: false, italic: false, underline: false }
  let buffer = ''
  const flush = () => {
    if (!buffer) return
    const span: CommentSpan = { text: buffer }
    if (format.bold) span.bold = true
    if (format.italic) span.italic = true
    if (format.underline) span.underline = true
    if (href) span.href = href
    out.push(span)
    buffer = ''
  }
  const toggle = (key: keyof Format, marker: string, at: number): boolean => {
    if (format[key]) {
      flush()
      format[key] = false
      return true
    }
    if (!hasCloser(s, marker, at + marker.length)) return false
    flush()
    format[key] = true
    return true
  }

  let i = 0
  while (i < s.length) {
    const ch = s[i]!
    if (ch === '\\' && i + 1 < s.length && ESCAPABLE.has(s[i + 1]!)) {
      buffer += s[i + 1]
      i += 2
      continue
    }
    if (s.startsWith('**', i)) {
      if (toggle('bold', '**', i)) {
        i += 2
        continue
      }
    } else if (ch === '*') {
      if (toggle('italic', '*', i)) {
        i += 1
        continue
      }
    } else if (s.startsWith('__', i)) {
      if (toggle('underline', '__', i)) {
        i += 2
        continue
      }
    } else if (ch === '[' && !href) {
      const close = findBracketClose(s, i + 1, ']')
      if (close !== -1 && s[close + 1] === '(') {
        const end = findBracketClose(s, close + 2, ')')
        if (end !== -1) {
          const url = s.slice(close + 2, end).trim()
          const label = s.slice(i + 1, close)
          const userId = MENTION_HREF_RE.exec(url)?.[1]
          if (userId && label.length > 0) {
            // `[@Name](user:id)` — also the lenient `@[Name](user:id)`, whose
            // `@` is the last character typed before the bracket.
            const inner: CommentSpan[] = []
            parseInline(label, undefined, inner)
            let name = inner.map((span) => span.text).join('')
            if (name.startsWith('@')) name = name.slice(1)
            else if (buffer.endsWith('@')) buffer = buffer.slice(0, -1)
            name = name.trim()
            if (name.length > 0) {
              flush()
              out.push({ text: `@${name}`, mention: { userId, name } })
              i = end + 1
              continue
            }
          }
          if (SAFE_HREF.test(url) && label.length > 0) {
            flush()
            const inner: CommentSpan[] = []
            parseInline(label, url, inner)
            for (const span of inner) {
              if (format.bold) span.bold = true
              if (format.italic) span.italic = true
              if (format.underline) span.underline = true
            }
            out.push(...inner)
            i = end + 1
            continue
          }
        }
      }
    }
    buffer += ch
    i++
  }
  flush()
}

/** Merge adjacent spans that render identically (same format and link). */
function mergeSpans(spans: CommentSpan[]): CommentSpan[] {
  const out: CommentSpan[] = []
  for (const span of spans) {
    const prev = out[out.length - 1]
    if (
      prev &&
      !prev.mention &&
      !span.mention &&
      !!prev.bold === !!span.bold &&
      !!prev.italic === !!span.italic &&
      !!prev.underline === !!span.underline &&
      prev.href === span.href
    ) {
      prev.text += span.text
    } else out.push({ ...span })
  }
  return out
}

/**
 * Markdown subset → spans (newlines stay inside the text; render with
 * `white-space: pre-wrap`). `format: 'text'` bodies come back verbatim as one span.
 */
export function parseCommentBody(body: string, format: CommentBodyFormat = 'md'): CommentSpan[] {
  if (format === 'text') return body ? [{ text: body }] : []
  const out: CommentSpan[] = []
  parseInline(body, undefined, out)
  return mergeSpans(out)
}

/** Plain text (notifications, previews); a mention becomes `@Name`. */
export function stripCommentMarkdown(body: string, format: CommentBodyFormat = 'md'): string {
  return parseCommentBody(body, format)
    .map((s) => s.text)
    .join('')
}

/** The members mentioned in a body, once each (first spelling of the name wins). */
export function commentMentions(body: string, format: CommentBodyFormat = 'md'): CommentMention[] {
  const seen = new Set<string>()
  const out: CommentMention[] = []
  for (const span of parseCommentBody(body, format)) {
    if (!span.mention || seen.has(span.mention.userId)) continue
    seen.add(span.mention.userId)
    out.push(span.mention)
  }
  return out
}

function escapeText(text: string): string {
  return text.replace(/[\\*_[\]]/g, (c) => `\\${c}`)
}

function wrap(text: string, span: CommentSpan): string {
  // Whitespace stays outside the markers (other markdown readers expect that).
  const lead = text.match(/^\s*/)?.[0] ?? ''
  const trail = text.match(/\s*$/)?.[0] ?? ''
  let inner = text.slice(lead.length, text.length - trail.length)
  if (!inner) return text
  if (span.underline) inner = `__${inner}__`
  if (span.italic) inner = `*${inner}*`
  if (span.bold) inner = `**${inner}**`
  return lead + inner + trail
}

function serializeSpans(spans: CommentSpan[]): string {
  let out = ''
  let i = 0
  const merged = mergeSpans(spans)
  while (i < merged.length) {
    const span = merged[i]!
    if (span.mention) {
      out += `[@${escapeText(span.mention.name)}](user:${span.mention.userId})`
      i++
      continue
    }
    if (span.href && SAFE_HREF.test(span.href)) {
      // Consecutive spans of the same link share one `[…](url)` (a mention ends the run).
      let j = i
      let label = ''
      while (j < merged.length && merged[j]!.href === span.href && !merged[j]!.mention) {
        label += wrap(escapeText(merged[j]!.text), merged[j]!)
        j++
      }
      const url = span.href.replace(
        /[\s()]/g,
        (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`
      )
      out += `[${label}](${url})`
      i = j
      continue
    }
    out += wrap(escapeText(span.text), span)
    i++
  }
  return out
}

/** Composer output → stored markdown subset. Paragraphs are joined with newlines. */
export function serializeCommentBody(lines: CommentLine[]): string {
  return lines.map(serializeSpans).join('\n').replace(/\s+$/, '')
}
